import React, { PureComponent } from 'react';
import { matchPath, withRouter } from 'react-router';
import cookie from 'js-cookie';
import { compose } from 'react-apollo';

import InAppModal from '~/components/InAppModal';

import { revalidateTrackingToken, getTrackingToken, getActivePage } from '~/utils';
import { API } from '~/constants';

import { mapProps } from './';
import query from '~/cache/query';
import mutation from '~/cache/mutation';
import withBusinessModel01Handling from './withBusinessModel01Handling';
import InAppPopup from '~/components/InAppPopup';
import InAppBar from '~/components/InAppBar';
import InAppSlideIn from '~/components/InAppSlideIn';
import InAppEmbeddedSurvey from '~/components/InAppEmbeddedSurvey';
import { CSSTransition } from 'react-transition-group';

const displayType = {
	MODAL: 'modal',
	POPUP: 'popup',
	BAR: 'bar',
	'SLIDE-IN': 'slide-in',
	'EMBEDDED-SURVEY': 'embedded-survey',
};

const withSendTracking = (Component) =>
	class extends PureComponent {
		state = {
			inAppContent: false,
			loading: false,
		};

		sendTracking = (id, name, event_version) => {
			const inAppContent = this.props.inAppContent.find(({ eventTriggers }) =>
				eventTriggers.some((event) => event === name),
			);

			if (inAppContent) {
				setTimeout(() => {
					switch (new URL(inAppContent?.url).pathname.split('/').pop()) {
						case 'sales':
							return this.props.openStripeUpgradeModal({
								salesVariant: 'iframe',
								iframeProps: {
									trigger: name,
									url: inAppContent.url,
									language: this.props.language,
								},
							});
						default:
							return this.setState({ inAppContent, inAppContentIsOpen: true });
					}
				}, inAppContent.timeout);
			}

			revalidateTrackingToken();
			const { token } = getTrackingToken();
			const session = cookie.get('session');
			const { path } = getActivePage(this.props.location.pathname);
			const { uid } = matchPath(this.props.location.pathname, {
				path,
				exact: true,
			}).params;

			const body = new FormData();
			body.append('event_id', id); // id, number
			body.append('event_name', name); // string
			body.append('version', 'v3'); // verze appky
			body.append('url', window.location.href); // string, aktuální url
			body.append('screen_resolution', `${window.innerWidth}x${window.innerHeight}`);
			body.append('user', this.props.email); // z API - login uživatele, pokud je
			body.append('session_id', token); // libovolný náhodný string, který bude zachován během jednoho přihlášení
			if (session) {
				body.append('web_session_id', session); // z cookies - session ID
			}
			if (uid) {
				body.append('survey_id', uid); // z API - UID dotazníku předávané v URL
			}
			if (event_version) {
				body.append('event_version', event_version); // string
			}

			const object = {};
			body.forEach(function (value, key) {
				object[key] = value;
			});

			if (window.env.SMARTLOOK === 'true') {
				smartlook('track', name, object);
			}

			return fetch(`${API}/tracking-ext`, {
				method: 'POST',
				body,
			});
		};

		renderInAppContent = (inAppContentIsOpen, inAppContent) => {
			if (!inAppContent) {
				return null;
			}

			const props = {
				inAppContentIsOpen,
				url: inAppContent.url,
				language: this.props.language,
				onClose: () => {
					this.props.closeInAppContent({ input: { id: inAppContent.id } });
					this.setState({ inAppContentIsOpen: false, inAppContent: false });
				},
			};

			const onExit = () => this.setState({ inAppContent: false });

			const onClose = () => {
				this.props.closeInAppContent({ input: { id: inAppContent.id } });
				this.setState({ inAppContentIsOpen: false });
			};

			switch (inAppContent.displayType) {
				default:
				case displayType.MODAL:
					return <InAppModal {...props} />;
				case displayType.POPUP:
					return <InAppPopup {...props} />;
				case displayType.BAR:
					return <InAppBar {...props} />;
				case displayType['SLIDE-IN']:
					return (
						<CSSTransition timeout={300} in={inAppContentIsOpen} onExited={onExit}>
							<InAppSlideIn {...props} onClose={onClose} />
						</CSSTransition>
					);
				case displayType['EMBEDDED-SURVEY']:
					return (
						<CSSTransition timeout={300} in={inAppContentIsOpen} onExited={onExit}>
							<InAppEmbeddedSurvey {...props} onClose={onClose} />
						</CSSTransition>
					);
			}
		};

		render() {
			return (
				<>
					<Component {...this.props.ownProps} sendTracking={this.sendTracking} />
					{this.renderInAppContent(this.state.inAppContentIsOpen, this.state.inAppContent)}
				</>
			);
		}
	};

export default compose(
	mapProps((ownProps) => ({ ownProps })),
	withRouter,
	mutation('closeInAppContent', {
		update: ({ cache, variables, getQuery }) => {
			const query = {
				query: getQuery('user/inAppContent'),
			};
			const { user } = cache.readQuery(query);
			cache.writeQuery({
				...query,
				data: {
					user: {
						...user,
						inAppContent: user.inAppContent.filter(({ id }) => variables.input.id !== id),
					},
				},
			});
		},
	}),
	query('account', {
		fragments: ['UserContact', 'Language'],
		mapProps: ({ user }) => ({ email: user?.email, language: user?.language }),
		options: () => ({
			variables: {},
		}),
	}),
	query('user/inAppContent', {
		mapProps: ({ user }) => ({ inAppContent: user?.inAppContent }),
	}),
	withBusinessModel01Handling,
	withSendTracking,
);
